import Hls from 'hls.js';
import { RefObject, useEffect } from 'react';
import type { Video } from 'expo-av';

import { useVar } from '@hooks';
import { getHTMLVideoElementFromRef } from '../videoPlayerUtils';
import { TokenService } from '@services';
import type {
  IVideoPlayerStream,
  IVideoPlayerStreamOptions,
} from './useVideoPlayerStream';

export function useVideoPlayerStream(
  videoRef: RefObject<Video>,
  options?: IVideoPlayerStreamOptions,
): IVideoPlayerStream {
  let hls = useVar<Hls | null>(null);
  let isRefreshed = useVar<boolean>(false);

  function setIsRefreshed(value: boolean) {
    isRefreshed = value;
  }

  useEffect(() => {
    const videoElement = getHTMLVideoElementFromRef(videoRef);

    if (!videoElement) {
      return;
    }

    // Check if the required MediaSource Extensions are available.
    // HLS.js is not supported on platforms that do not have MediaSource
    // Extensions (MSE) enabled.
    if (Hls.isSupported()) {
      hls = new Hls({
        async xhrSetup(xhr) {
          const tokens = await TokenService.get();

          if (tokens) {
            xhr.setRequestHeader(
              'Authorization',
              `Bearer ${tokens.accessToken}`,
            );
          }
        },
      });
      hls.attachMedia(videoElement);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          if (data.response?.code === 403) {
            refreshTokenAndLoadSource();
          }
        }
      });

      loadSource();

      // If no HLS.js support, check for native browser HLS support.
    } else if (
      videoElement.canPlayType('application/vnd.apple.mpegurl')
    ) {
      videoElement.addEventListener('error', () => {
        refreshTokenAndLoadSource();
      });

      loadSource();
    }
  }, []);

  async function loadSource() {
    if (typeof options?.source !== 'object') {
      return;
    }

    const videoElement = getHTMLVideoElementFromRef(videoRef);

    // If there is no hls at this point it means that HLS.js is not supported
    if (hls) {
      hls.loadSource(options.source.uri);
      // If no HLS.js support, check for native browser HLS support
    } else if (
      videoElement &&
      videoElement.canPlayType('application/vnd.apple.mpegurl')
    ) {
      const tokens = await TokenService.get();

      if (tokens) {
        // When the browser has built-in HLS support (check using `canPlayType`),
        // we can provide an HLS manifest (i.e. .m3u8 URL) directly to the video
        // element through the `src` property. This is using the built-in support
        // of the plain video element, without using HLS.js.
        videoElement.src = `${options.source.uri}/${tokens.accessToken}`;
      }
    }
  }

  async function refreshTokenAndLoadSource() {
    if (isRefreshed) {
      return;
    }

    if (options?.onRefreshTokens) {
      await options.onRefreshTokens();
      setIsRefreshed(true);
      loadSource();
    } else {
      setIsRefreshed(true);
    }
  }

  return {
    loadFailed: () => {},
  };
}
